import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { AlertTriangle, Wrench, FileText, ArrowRight, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import Button from './ui/Button';
import { fadeInUp, stagger } from '../lib/animations';
import { getAllPosts } from '../lib/posts';

const ERROR_MESSAGES = [
  { title: "Rua sem saída!", text: "Essa página pegou o retorno errado e não foi encontrada." },
  { title: "Pneu furou!", text: "Não conseguimos carregar o conteúdo dessa página. O pneu furou no caminho." },
  { title: "Retorno errado!", text: "Parece que você pegou a saída errada na rotatória da internet." },
  { title: "Pane Seca!", text: "Faltou combustível para carregar esta página." },
  { title: "Motor fundiu!", text: "Isso é que dá passar do limite de giro... a página não existe." },
];

const NotFound: React.FC = () => {
  const [errorMsg, setErrorMsg] = useState(ERROR_MESSAGES[0]);
  const [latestPost] = useState(() => {
    const posts = getAllPosts();
    return posts.length > 0 ? posts[0] : null;
  });

  useEffect(() => {
    const randomMsg = ERROR_MESSAGES[Math.floor(Math.random() * ERROR_MESSAGES.length)];
    setErrorMsg(randomMsg);
  }, []);

  return (
    <>
      <Helmet>
        <title>Página não encontrada – Gas2</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Navbar solid />

      <main className="pt-32 pb-24 min-h-[70vh] flex flex-col justify-center items-center px-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="max-w-2xl w-full mx-auto"
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-amber-100 text-amber-500 p-4 rounded-full">
                <AlertTriangle className="h-12 w-12" strokeWidth={1.8} />
              </div>
            </div>

            <h1 className="font-display font-bold text-4xl md:text-5xl text-gray-900 leading-tight mb-4 tracking-tight">
              {errorMsg.title}
            </h1>
            <p className="text-gray-600 text-lg sm:text-xl">
              Erro 404: {errorMsg.text}
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 text-left">
            {/* Suggestion 1: Ferramentas */}
            <Link to="/ferramentas" className="group p-6 bg-white border border-gray-100 hover:border-amber-400 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="bg-amber-50 text-amber-500 p-3 w-max rounded-xl mb-4 group-hover:bg-amber-400 group-hover:text-amber-900 transition-colors">
                <Wrench className="h-6 w-6" strokeWidth={1.8} />
              </div>
              <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">
                Ferramentas Automotivas
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Calcule gastos, valide pneus e organize a manutenção do seu veículo gratuitamente.
              </p>
              <div className="text-amber-500 group-hover:text-amber-600 font-semibold flex items-center gap-2 text-sm">
                Acessar Ferramentas <ArrowRight className="h-4 w-4" />
              </div>
            </Link>

            {/* Suggestion 2: Blog ou Último Artigo */}
            {latestPost ? (
              <Link to={`/blog/${latestPost.slug}`} className="group p-6 bg-white border border-gray-100 hover:border-amber-400 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="bg-blue-50 text-blue-500 p-3 w-max rounded-xl mb-4 group-hover:bg-blue-100 transition-colors">
                  <FileText className="h-6 w-6" strokeWidth={1.8} />
                </div>
                <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">
                  Nosso último artigo
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {latestPost.title}
                </p>
                <div className="text-blue-500 group-hover:text-blue-600 font-semibold flex items-center gap-2 text-sm">
                  Ler Artigo <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ) : (
              <Link to="/blog" className="group p-6 bg-white border border-gray-100 hover:border-amber-400 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="bg-blue-50 text-blue-500 p-3 w-max rounded-xl mb-4 group-hover:bg-blue-100 transition-colors">
                  <FileText className="h-6 w-6" strokeWidth={1.8} />
                </div>
                <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">
                  Blog do Gas2
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Dicas de economia, guias de manutenção e novidades automotivas.
                </p>
                <div className="text-blue-500 group-hover:text-blue-600 font-semibold flex items-center gap-2 text-sm">
                  Acessar Blog <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            )}
          </motion.div>

          <motion.div variants={fadeInUp} className="flex justify-center flex-col sm:flex-row gap-4">
            <Button href="/" variant="primary" icon={Home}>
              Voltar ao Início
            </Button>
            <Button href="/blog" variant="outline" icon={FileText}>
              Ver todos os artigos
            </Button>
          </motion.div>
        </motion.div>
      </main>

      <Footer />
    </>
  );
};

export default NotFound;

